import type { ABTestScenario } from "@reco/shared/src/types/abTest";
import type { GridBox } from "@reco/shared/src/types/gridbox";
import type { Recommendation, RecommendationsConfig } from "@reco/shared/src/types/recommendation";

import { Scenario } from "@reco/shared/src/constants";
import { getTrackingPageType, PageType } from "./pageType";

const BLOCKED_ERPs = new Set([
  "100266861",
  "100266862",
  "100245615",
  "100266878",
  "100216475",
  "100266865",
  "100266863",
  "100244842",
  "100250560",
  "100318448",
  "100227811",
  "100250558",
  "100344176",
  "100343925",
  "100249068",
  "100266514",
]);
const BADGE_TYPES = new Set(["ALSO_IN_STORE", "IN_STORE", "IN_STORE_AS_OF"]);

const getProductTrackingInfo = (
  scenario: Scenario,
  abTest?: ABTestScenario,
  ssoId?: string,
  lidlId?: string,
): string => {
  const abTestValue = abTest?.group ? `_${abTest.group.toLowerCase()}` : "";
  const pageType = getTrackingPageType();

  let trackingScenario = scenario as string;

  if (scenario === Scenario.PERSONALIZATION) {
    trackingScenario = "personalization_fe_views";

    if (ssoId) {
      trackingScenario = "customer_account";
    } else if (lidlId) {
      trackingScenario = "customer_cookie";
    }
  }

  return `reco_${pageType}_${trackingScenario}${abTestValue}`;
};

const addBeaconsProperties = (recommendation: GridBox, recommendationItems: Recommendation[]): GridBox => {
  const recommendationItem = recommendationItems.find((item) => recommendation.erpNumber === item.itemId);
  return recommendationItem?.beacons
    ? {
        ...recommendation,
        ...recommendationItem.beacons,
      }
    : recommendation;
};

const mergePGBProps = (recommendation: GridBox, propsToAdd: object, index: number) =>
  Object.assign(recommendation, propsToAdd, {
    position: index + 1,
  });

const removeFlashSales = (recommendation: GridBox) => !recommendation.flashSales;

const removeBlockedProducts = (recommendation: GridBox) => !BLOCKED_ERPs.has(recommendation.erpNumber);

const isNotMultiPack = (recommendation: GridBox): boolean => !recommendation.multipack;

const removeDuplicates = (recommendation: GridBox, index: number, self: GridBox[]) =>
  self.findIndex((t) => t.erpNumber === recommendation.erpNumber) === index;

const removePGBProps = (recommendation: GridBox) => {
  if (recommendation.dealOfDay) {
    recommendation.dealOfDay.active = false;
  }
  delete recommendation?.ribbons;
  delete recommendation?.keyfacts?.supplementalDescription;
  return recommendation;
};

const removeBadgeInfoInStore = (recommendation: GridBox) => {
  const badges = recommendation?.stockAvailability?.badgeInfo?.badges;
  if (!badges) {
    return recommendation;
  }

  const filteredBadges = badges.filter((badge) => !badge.type || !BADGE_TYPES.has(badge.type));
  recommendation.stockAvailability.badgeInfo.badges = filteredBadges;
  return recommendation;
};

export function prepareResponse(params: RecommendationsConfig): GridBox[] {
  const isBasketOverlay = params.scenario === Scenario.CROSS && params.pageType === PageType.PDP;

  const addPGBProps = {
    hashParams: `fromRecommendation=true&scenario=${params.scenario}`,
    list: getProductTrackingInfo(params.scenario, params.abTest, params.ssoId, params.lidlId),
    locale: params.locale,
  };

  return params.recommendations
    .filter(params.scenario === Scenario.BUNDLES ? isNotMultiPack : () => true)
    .filter(params.scenario === Scenario.COMPARISONS ? removeDuplicates : () => true)
    .filter(removeBlockedProducts)
    .filter(removeFlashSales)
    .map(params.min ? removePGBProps : (elem) => elem)
    .map(isBasketOverlay ? removeBadgeInfoInStore : (elem) => elem)
    .map((rec) =>
      params.scenario === Scenario.SPONSORED ? addBeaconsProperties(rec, params.recommendationItems) : rec,
    )
    .map((rec, index) => mergePGBProps(rec, addPGBProps, index))
    .slice(0, params.displayLimit);
}

export async function fetchWithTimeout(uri: string, options: RequestInit = {}, time = 5000): Promise<Response> {
  let config = {
    ...options,
  };
  let timeoutId: ReturnType<typeof setTimeout>;
  if (typeof globalThis.AbortController === "function") {
    const controller = new AbortController();
    config = {
      ...options,
      signal: controller.signal,
    };

    const startTime = Date.now();

    const abortFunc = () => {
      const currTime = Date.now();
      if (currTime - startTime >= time) {
        controller.abort();
        if (timeoutId) clearTimeout(timeoutId);
      } else {
        let remainingTime = time - (currTime - startTime);
        if (remainingTime < 0) {
          remainingTime = 0;
        }
        timeoutId = setTimeout(abortFunc, remainingTime);
      }
    };
    abortFunc();
  }

  return fetch(uri, config)
    .then((response) => {
      if (timeoutId) clearTimeout(timeoutId);
      if (!response.ok) {
        throw new Error(`${response.status}: ${response.statusText}`);
      }
      return response;
    })
    .catch((error) => {
      if (timeoutId) clearTimeout(timeoutId);
      if (error.name === "AbortError") {
        throw new Error("Response timed out");
      }

      throw new Error(error.message);
    });
}
